import { Experiment } from "@amplitude/experiment-js-client";
import * as amplitude from "@amplitude/analytics-browser";
import type { IntegrationPlugin } from "@amplitude/experiment-js-client";
import type { ABTestCampaignId } from "@/data/abTestCampaigns.data";
import { useAuthStore } from "@/stores/useAuth";
import {
  AB_TEST_CAMPAIGNS_DATA,
  EXPERIMENT_EXPOSURE_EVENT,
  EXPERIMENT_IDS,
} from "@/data/abTestCampaigns.data";
import PricingService from "@/lib/pricingService";
import { getStoredFunnelFlow, isStandaloneFlow } from "@/lib/funnelFlow";
import { runWhenIdle } from "@/lib/runWhenIdle";
import { captureForTest } from "@/lib/trackingTestCapture";

/** How long a first visit waits for its variants before the first render (SHO-852). */
export const EXPERIMENT_BOOT_GATE_MS = 1500;

// Not `initializeWithAmplitudeAnalytics`: its fetch waits for an analytics
// identity that the deferred `amplitude.init` only creates after `load`.
// `throwOnError`: the SDK otherwise swallows every failure and `fetch()`
// resolves, which would apply an empty answer and end the gate for good.
const experiment = Experiment.initialize(
  import.meta.env.VITE_AMPLITUDE_EXPERIMENT_API_KEY,
  { throwOnError: true },
);

// The analytics integration by hand: no `setup`, so the fetch never waits
// for an identity; `track` returns true so the SDK's persistent exposure
// queue drains, and its per-session dedupe applies. `$exposure` tracked
// before `amplitude.init` queues in the analytics SDK (`experiments.md`).
const integration: IntegrationPlugin = {
  type: "integration",
  getUser: () => ({}),
  track: (event) => {
    amplitude.track(event.eventType, event.eventProperties);
    return true;
  },
};
experiment.addPlugin(integration);

// A standalone funnel (exp_wmq) pins its own pricing, so it stays out of
// every Amplitude experiment.
const isStandalone = () => isStandaloneFlow(getStoredFunnelFlow());

const currentUserId = () => useAuthStore.getState().currentUser?.id || "";

/**
 * Reports `[Experiment] Exposure` for a `manual` campaign, from the place
 * where its variant decides something. The SDK dedupes per flag and variant
 * for the tab session, so calling it on every decision is what "once" means.
 */
export const trackExperimentExposure = (campaignId: ABTestCampaignId) => {
  // No exposure for a standalone funnel, which reads the control whatever
  // the SDK stored (see `useIsABTestVariant`), nor for a visit no answer was
  // applied to: the SDK may hold the late answer a lost gate dropped.
  const { funnelFlow, abTestVariants } = useAuthStore.getState();
  if (isStandaloneFlow(funnelFlow) || abTestVariants === null) return;
  experiment.exposure(campaignId);
  // The SDK's own event never reaches the capture buffer; this mirrors the
  // call so the contract specs can see when the exposure was asked for.
  captureForTest("amplitude", EXPERIMENT_EXPOSURE_EVENT, {
    flag_key: campaignId,
  });
};

/** Whether the server answered this user's variants; nothing to ask resolves true at once. */
const fetchSucceeded = async (userId?: string) => {
  if (isStandalone() || EXPERIMENT_IDS.length === 0) return true;
  const user_id = userId || currentUserId();
  // No user, no answer: nothing to apply, the next visit gates again.
  if (!user_id) return false;
  try {
    // The whole deployment, not `flagKeys`: the SDK never drops from its own
    // storage a key the answer omits (its `storeVariants` iterates the
    // array's indices), so a flag the deployment stops serving would read as
    // its last variant for ever.
    await experiment.fetch({ user_id });
    return true;
  } catch (error) {
    console.error("Error fetching AB test variants:", error);
    return false;
  }
};

/** Copies the fetched variants into the store and the Amplitude user properties. */
const applyABTestVariants = () => {
  if (isStandalone()) return;
  const { setABTestVariant } = useAuthStore.getState();
  // `variant()` reports an exposure on every read; `all()` reports none.
  const unexposed = experiment.all();
  AB_TEST_CAMPAIGNS_DATA.forEach((campaign) => {
    const variant =
      campaign.exposure === "manual"
        ? (unexposed[campaign.id] ?? {})
        : experiment.variant(campaign.id);
    // No variant (the flag is disabled or rolled back) or `off` both mean
    // the control: a value persisted by an earlier visit must not outlive
    // the flag — neither in the store nor as the Amplitude user property
    // that attributes this user's events to the variant.
    if (!variant.key || variant.key === "off") {
      setABTestVariant(campaign.id, undefined);
      PricingService.removeAmplitudeUserProperty(campaign.id);
      return;
    }
    const campaignVariant = campaign.variants.find(
      (v) => v.value === variant.key,
    );
    setABTestVariant(campaign.id, campaignVariant?.name);
    PricingService.addAmplitudeUserProperty(campaign.id, variant.key);
  });
};

/**
 * The boot read. A first visit (`null`: no answer ever applied; an applied
 * one leaves at least `{}`) waits for the answer, within the gate, so the
 * first render reads what the exposure will say; a lost gate drops the
 * answer, so the next visit gates again. A later visit renders on the stored
 * variants and refreshes them once the page is idle, off the first paint.
 */
const bootABTestVariants = async () => {
  // The SDK's user, before any exposure: only a fetch sets it otherwise, and
  // the exposure dedupe resets whenever the identity changes, so an exposure
  // asked for before the idle fetch of a later visit would be sent twice.
  const user_id = currentUserId();
  if (user_id) experiment.setUser({ user_id });
  if (useAuthStore.getState().abTestVariants !== null) {
    runWhenIdle(() => {
      void fetchSucceeded().then((ok) => ok && applyABTestVariants());
    });
    return;
  }
  const gate = new Promise<boolean>((resolve) =>
    setTimeout(resolve, EXPERIMENT_BOOT_GATE_MS, false),
  );
  if (await Promise.race([fetchSucceeded(), gate])) applyABTestVariants();
};

/** The login read: the new user's variants, applied once answered. */
const setUserABTestVariants = async (userId?: string) => {
  if (await fetchSucceeded(userId)) applyABTestVariants();
};

export const useAmplitudeExperiment = () => ({
  bootABTestVariants,
  setUserABTestVariants,
});
